import { SaudiRiyalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@virtual-care/shadcn';

import type { PaymentInfo } from '../../hooks/use-payment-info.ts';

type PaymentDetailsCardProps = {
  payment: PaymentInfo;
};

type PaymentRowProps = {
  label: string;
  value?: string | number;
  isTotal?: boolean;
};

type PillProps = {
  label: string;
  value?: string | number;
};

export const PaymentDetailsCard = ({ payment }: PaymentDetailsCardProps) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border bg-white p-6">
      <h3 className="mb-2 text-lg font-bold text-gray-900">
        {t('pricing.paymentDetailsTitle')}
      </h3>
      <div>
        <PaymentRow
          label={t('pricing.totalAmount')}
          value={payment.total.shortLabel}
        />
        <PaymentRow
          label={t('pricing.insuranceDiscount')}
          value={payment.insuranceDiscount.shortLabel}
        />
        {payment.appliedPromoCode && (
          <PaymentRow
            label={`${t('pricing.promoDiscount')} (${payment.appliedPromoCode})`}
            value={`-${payment.promoDiscount.shortLabel}`}
          />
        )}
        <PaymentRow label={t('pricing.vat')} value={payment.vat.shortLabel} />
        <PaymentRow
          label={t('pricing.amountDue')}
          value={payment.amountDue.shortLabel}
          isTotal
        />
      </div>
    </div>
  );
};

const PaymentRow = ({ label, value, isTotal = false }: PaymentRowProps) => (
  <div
    className={`flex items-center justify-between border-b py-3 last:border-none ${isTotal ? 'text-lg font-bold' : 'text-sm'}`}
  >
    <span className="text-gray-600">{label}</span>
    <span
      className={`flex items-center justify-center gap-1 ${isTotal ? 'text-primary' : 'text-sm'}`}
    >
      {value}
      <SaudiRiyalIcon
        className={cn(
          isTotal ? 'text-primary size-6' : 'h-4 w-4 text-gray-900',
        )}
      />
    </span>
  </div>
);

export const DetailPill = ({ label, value }: PillProps) => (
  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-sm">
    <span className="font-semibold text-gray-800">{label}</span>
    <span className="mx-2 text-gray-600">{value}</span>
  </div>
);

export const AppointmentPill = ({ label, value }: PillProps) => (
  <div className="flex items-center rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm">
    <span className="font-semibold text-gray-800">{label}</span>
    <span className="mx-2 text-gray-600">{value}</span>
  </div>
);
